// Thin API client. The token and chosen store live in localStorage so a register survives a reload.
export interface Session {
  token: string;
  user: { id: string; name: string; role: 'platform_admin' | 'owner' | 'manager' | 'cashier'; email?: string };
  stores: { id: string; name: string }[];
  storeId: string;
}

const KEY = 'njpos.session';

export function loadSession(): Session | null {
  try { return JSON.parse(localStorage.getItem(KEY) ?? 'null'); } catch { return null; }
}
export function saveSession(s: Session | null) {
  if (s) localStorage.setItem(KEY, JSON.stringify(s)); else localStorage.removeItem(KEY);
  window.dispatchEvent(new Event('njpos:session'));
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public detail?: any) { super(message); }
}
export class NetworkError extends Error {}

export async function api<T = any>(path: string, init: { method?: string; body?: unknown; form?: FormData; raw?: boolean } = {}): Promise<T> {
  const s = loadSession();
  const headers: Record<string, string> = {};
  if (s) { headers.authorization = `Bearer ${s.token}`; headers['x-store-id'] = s.storeId; }
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(path, { method: init.method ?? (init.body !== undefined || init.form ? 'POST' : 'GET'), headers, body: init.form ?? (init.body !== undefined ? JSON.stringify(init.body) : undefined) });
  } catch {
    throw new NetworkError('No connection to the server');
  }
  if (res.status === 401 && s) { saveSession(null); }
  if (init.raw) return res as unknown as T;
  const text = await res.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) throw new ApiError(res.status, data?.error ?? 'ERROR', data?.message ?? `Request failed (${res.status})`, data?.detail);
  return data as T;
}

export const usd = (c: number | null | undefined) =>
  c == null ? '—' : `${c < 0 ? '−' : ''}$${(Math.abs(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const usd0 = (c: number | null | undefined) =>
  c == null ? '—' : `${c < 0 ? '−' : ''}$${Math.round(Math.abs(c) / 100).toLocaleString('en-US')}`;

export const sizeLabel = (ml: number) => (ml >= 1000 ? `${ml / 1000}L` : ml === 375 ? '375ml' : `${ml}ml`);

export const monthLabel = (m: string) => new Date(`${m}T12:00:00Z`).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
/** '2026-11-01' -> 'November 1' */
export const dayLabel = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
